import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}`;

const getAuthKey = () => {
  return `${process.env.REACT_APP_API_SECURITY_KEY}`;
};

const DynamicForm = ({ fileId }) => {
  const [fields, setFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [showNeighborsForm, setShowNeighborsForm] = useState(false);
  const [familyMembers, setFamilyMembers] = useState([{}]);

  // Fetch form fields when the component mounts or fileId changes
  useEffect(() => {
    const fetchFields = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/verificationFields/${fileId}`, {
          headers: {
            'auth_key': `${getAuthKey()}`,
          },
        });
        setFields(response.data);
      } catch (error) {
        console.error('Error fetching form fields:', error);
      }
    };

    fetchFields();
  }, [fileId]);

  // Handle input changes for all fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Show neighbors form if "Address Confirmed" is set to "Locked"
    if (name === 'addressConfirmed' && value === 'Locked') {
      setShowNeighborsForm(true);
    } else if (name === 'addressConfirmed') {
      setShowNeighborsForm(false);
    }
  };

  // Handle change for dynamic family members
  const handleFamilyMemberChange = (index, e) => {
    const { name, value } = e.target;
    const updatedFamilyMembers = [...familyMembers];
    updatedFamilyMembers[index][name] = value;
    setFamilyMembers(updatedFamilyMembers);
  };

  // Add more family members dynamically
  const addFamilyMember = () => {
    setFamilyMembers([...familyMembers, {}]);
  };

  // Render form fields dynamically based on their type
  const renderField = (field) => {
    const { fieldId, fieldLabel, fieldName, fieldType, isRequired, options } = field;

    switch (fieldType) {
      case 'text':
      case 'number':
      case 'date':
        return (
          <div key={fieldId}>
            <label htmlFor={fieldName} className="block text-sm font-medium text-gray-700">
              {fieldLabel}
            </label>
            <input
              type={fieldType}
              id={fieldName}
              name={fieldName}
              required={isRequired}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
              onChange={handleInputChange}
            />
          </div>
        );
      case 'select':
        return (
          <div key={fieldId}>
            <label htmlFor={fieldName} className="block text-sm font-medium text-gray-700">
              {fieldLabel}
            </label>
            <select
              id={fieldName}
              name={fieldName}
              required={isRequired}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
              onChange={handleInputChange}
            >
              <option value="">Select...</option>
            {options.map((option) => (
              <option key={option.optionId} value={option.optionValue}>
                {option.optionLabel}
              </option>
            ))}
            </select>
          </div>
        );
        case 'checkbox':
            return (
              <div key={fieldId} className="mb-4">
                <label className="block text-sm font-medium text-gray-700">{fieldLabel}</label>
                <div className="mt-2">
                  {options.map((option) => (
                    <div key={option.optionId} className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        id={`${fieldName}-${option.optionValue}`}
                        name={fieldName}
                        value={option.optionValue}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        onChange={(e) => handleInputChange(e)}
                      />
                      <label htmlFor={`${fieldName}-${option.optionValue}`} className="ml-2 text-sm text-gray-600">
                        {option.optionLabel}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            );
      
      case 'radio':
        return (
          <div key={fieldId}>
            <label className="block text-sm font-medium text-gray-700">{fieldLabel}</label>
            {field.options &&
              field.options.map((option, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id={`${fieldName}-${index}`}
                    name={fieldName}
                    value={option.value}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                    onChange={handleInputChange}
                  />
                  <label htmlFor={`${fieldName}-${index}`} className="text-sm text-gray-600">
                    {option.label}
                  </label>
                </div>
              ))}
          </div>
        );
      case 'textarea':
        return (
          <div key={fieldId}>
            <label htmlFor={fieldName} className="block text-sm font-medium text-gray-700">
              {fieldLabel}
            </label>
            <textarea
              id={fieldName}
              name={fieldName}
              required={isRequired}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
              onChange={handleInputChange}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div class="container mx-auto p-6 bg-white rounded-lg border">
    <form>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {fields.map((field) => renderField(field))}
      </div>

      {/* Neighbors Form */}
      {showNeighborsForm && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold">Neighbors Form</h3>
          {familyMembers.map((_, index) => (
            <div key={index} className="border p-4 mt-4 rounded-lg">
              <div className="mb-4">
                <label htmlFor={`doesApplicantStay-${index}`} className="block text-sm font-medium text-gray-700">
                  Does applicant stay at this residence?
                </label>
                <select
                  id={`doesApplicantStay-${index}`}
                  name={`doesApplicantStay-${index}`}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                  onChange={(e) => handleFamilyMemberChange(index, e)}
                >
                  <option value="">Select...</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>

              <div className="mb-4">
                <label htmlFor={`approximateAge-${index}`} className="block text-sm font-medium text-gray-700">
                  Approximate age of applicant
                </label>
                <input
                  type="number"
                  id={`approximateAge-${index}`}
                  name={`approximateAge-${index}`}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                  onChange={(e) => handleFamilyMemberChange(index, e)}
                />
              </div>

              <div className="mb-4">
                <label htmlFor={`familyMembersCount-${index}`} className="block text-sm font-medium text-gray-700">
                  Number of family members
                </label>
                <input
                  type="number"
                  id={`familyMembersCount-${index}`}
                  name={`familyMembersCount-${index}`}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                  onChange={(e) => handleFamilyMemberChange(index, e)}
                />
              </div>
            </div>
          ))}

          <button
            type="button"
            className="mt-4 bg-green-500 text-white px-4 py-2 rounded-md"
            onClick={addFamilyMember}
          >
            Add Another Family Member
          </button>
        </div>
      )}

      <button type="submit" className="mt-6 bg-blue-500 text-white px-4 py-2 rounded-md">
        Submit
      </button>
    </form>
    </div>
  );
};

export default DynamicForm;
